// Serialization, redaction and truncation helpers for support reports.
// Ported from cartadigital-pos app/utils/support-report.ts.

export interface IMakeSerializableOptions {
  /** Max nesting depth when converting complex objects for JSON. */
  maxDepth?: number;
  /** Max items per array; extra items are replaced by a truncation marker. */
  maxArrayItems?: number;
  /** Max length for regular strings. */
  maxStringLength?: number;
  /** Max length for error stacks (stacks can be large). */
  maxErrorStackLength?: number;
}

export type ISafeAsyncResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toErrorMessage(e: unknown, fallbackMessage: string): string {
  if (e instanceof Error && e.message) return e.message;
  if (typeof e === 'string' && e.trim()) return e;
  return fallbackMessage;
}

export function truncateString(value: string, max = 8000): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max)}… [truncated ${value.length - max} chars]`;
}

export function redactSecretsInString(input: string): string {
  let out = input;

  // Bearer tokens (common)
  out = out.replace(/Bearer\s+[^\s]+/gi, 'Bearer [REDACTED]');

  // Common sensitive query params
  out = out.replace(
    /([?&](?:access_token|refresh_token|id_token|token|apikey|api_key|password)=)[^&]+/gi,
    '$1[REDACTED]'
  );

  return out;
}

export function sanitizeString(value: string, max = 8000): string {
  return truncateString(redactSecretsInString(value), max);
}

export function shouldRedactKey(key: string): boolean {
  const k = key.trim().toLowerCase();
  if (!k) return false;

  if (k === 'authorization' || k === 'cookie' || k === 'set-cookie')
    return true;
  if (k === 'password' || k === 'passwd') return true;
  if (k === 'secret' || k === 'client_secret') return true;
  if (k === 'api_key' || k === 'apikey' || k === 'x-api-key') return true;
  if (k.includes('token')) return true;

  return false;
}

function toSerializable(
  value: unknown,
  options: {
    depth: number;
    maxDepth: number;
    maxArrayItems: number;
    maxStringLength: number;
    maxErrorStackLength: number;
    seen: WeakSet<object>;
  }
): unknown {
  const {
    depth,
    maxDepth,
    maxArrayItems,
    maxStringLength,
    maxErrorStackLength,
    seen,
  } = options;

  if (value == null) return null;
  if (typeof value === 'string') return sanitizeString(value, maxStringLength);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'function') return '[Function]';
  if (typeof value === 'symbol') return value.toString();
  if (value instanceof Date) return value.toISOString();

  if (value instanceof Error) {
    // IMPORTANT: handle circular error causes safely
    if (seen.has(value)) return '[CircularError]';
    seen.add(value);

    const anyErr = value as Error & Record<string, unknown>;
    const out: Record<string, unknown> = {
      name: value.name,
      message: sanitizeString(value.message, maxStringLength),
      stack: value.stack
        ? sanitizeString(value.stack, maxErrorStackLength)
        : undefined,
    };

    // Preserve `cause` even if it's non-enumerable. Read via the widened
    // alias: the project TS lib target predates Error.cause typings.
    const cause = anyErr.cause;
    if (cause !== undefined) {
      out.cause = toSerializable(cause, {
        depth: depth + 1,
        maxDepth,
        maxArrayItems,
        maxStringLength,
        maxErrorStackLength,
        seen,
      });
    }

    // Include enumerable extra fields (libs often add metadata here)
    for (const k of Object.keys(anyErr)) {
      if (k in out) continue;
      if (k === 'cause') continue;
      if (shouldRedactKey(k)) {
        out[k] = '[REDACTED]';
        continue;
      }
      out[k] = toSerializable(anyErr[k], {
        depth: depth + 1,
        maxDepth,
        maxArrayItems,
        maxStringLength,
        maxErrorStackLength,
        seen,
      });
    }

    return out;
  }

  if (typeof value === 'object') {
    if (seen.has(value)) return '[Circular]';
    seen.add(value);
  }

  if (depth >= maxDepth) return '[MaxDepth]';

  if (Array.isArray(value)) {
    const sliced = value.slice(0, maxArrayItems).map((v) =>
      toSerializable(v, {
        depth: depth + 1,
        maxDepth,
        maxArrayItems,
        maxStringLength,
        maxErrorStackLength,
        seen,
      })
    );
    if (value.length > maxArrayItems) {
      sliced.push(`[Truncated: ${value.length - maxArrayItems} more items]`);
    }
    return sliced;
  }

  if (!isRecord(value)) return sanitizeString(String(value), maxStringLength);

  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value)) {
    if (shouldRedactKey(k)) {
      out[k] = '[REDACTED]';
      continue;
    }
    out[k] = toSerializable(v, {
      depth: depth + 1,
      maxDepth,
      maxArrayItems,
      maxStringLength,
      maxErrorStackLength,
      seen,
    });
  }
  return out;
}

export function makeSerializable(
  value: unknown,
  options: IMakeSerializableOptions = {}
): unknown {
  return toSerializable(value, {
    depth: 0,
    maxDepth: options.maxDepth ?? 5,
    maxArrayItems: options.maxArrayItems ?? 50,
    maxStringLength: options.maxStringLength ?? 8000,
    maxErrorStackLength: options.maxErrorStackLength ?? 20000,
    seen: new WeakSet<object>(),
  });
}

export async function safeAsync<T>(
  fn: () => Promise<T>,
  options?: { fallbackMessage?: string }
): Promise<ISafeAsyncResult<T>> {
  try {
    return { ok: true, value: await fn() };
  } catch (e) {
    return {
      ok: false,
      error: toErrorMessage(e, options?.fallbackMessage ?? 'Unknown error'),
    };
  }
}

export function stringifyForReport(
  value: unknown,
  options?: IMakeSerializableOptions & { space?: number }
): string {
  const space = typeof options?.space === 'number' ? options.space : 2;
  const serializable = makeSerializable(value, options);
  return JSON.stringify(serializable, null, space);
}
